import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const initialFormState = {
  username: '',
  password: '',
};

const INITIAL_PASSWORD_ROUTE = '/initial-password';
const DEV_CREDENTIALS = {
  username: 'admin',
  password: 'admin',
};

const isLocalDevHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, error, clearError, authenticating, isAuthenticated } = useAuth();
  const [form, setForm] = useState(initialFormState);
  const [submitError, setSubmitError] = useState(null);
  const showDevLogin = isLocalDevHost();

  const redirectTarget = useMemo(() => {
    if (location.state?.from && typeof location.state.from.pathname === 'string') {
      return location.state.from.pathname;
    }
    return '/';
  }, [location.state]);

  useEffect(() => {
    setSubmitError(error || null);
  }, [error]);

  useEffect(() => () => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTarget, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTarget]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);
    try {
      await login({ username: form.username, password: form.password });
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      const fallbackMessage = 'Не удалось выполнить вход. Попробуйте еще раз.';
      const status = err?.response?.status;
      if (status === 401) {
        setSubmitError('Неверное имя пользователя или пароль.');
      } else if (status === 423) {
        setSubmitError(
          <>
            Пароль для этой учётной записи ещё не установлен.{' '}
            <MuiLink component={RouterLink} to={INITIAL_PASSWORD_ROUTE}>
              Откройте страницу установки пароля
            </MuiLink>{' '}
            и используйте одноразовый токен.
          </>,
        );
      } else {
        const isNetworkError = !err?.response?.status;
        const message = err?.response?.data?.message
          || err?.response?.data?.error
          || (isNetworkError ? 'Сервис запускается или временно недоступен. Повторите через несколько секунд.' : null)
          || err?.message
          || fallbackMessage;
        setSubmitError(message);
      }
    }
  };

  const handleDevLogin = async () => {
    setSubmitError(null);
    setForm(DEV_CREDENTIALS);
    try {
      await login(DEV_CREDENTIALS);
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Dev-вход не выполнен.');
    }
  };

  const isSubmitDisabled = authenticating || form.username.trim() === '' || form.password.trim() === '';

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 5, md: 9 } }}>
      <Paper
        elevation={0}
        className="surface-glow page-enter"
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: 180,
            height: 180,
            borderRadius: '50%',
            top: -80,
            right: -60,
            background: 'radial-gradient(circle, rgba(233,79,55,0.18) 0%, rgba(233,79,55,0) 70%)',
            pointerEvents: 'none',
          }}
        />
        <Stack spacing={0.5} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Контур качества
          </Typography>
          <Typography variant="h4" component="h1">
            Вход
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Введите учетные данные для доступа к панели управления.
          </Typography>
        </Stack>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Имя пользователя"
              name="username"
              value={form.username}
              onChange={handleChange}
              disabled={authenticating}
              autoComplete="username"
              required
              fullWidth
            />
            <TextField
              label="Пароль"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              disabled={authenticating}
              autoComplete="current-password"
              required
              fullWidth
            />
            {submitError && (
              <Alert severity="error" onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              size="large"
              className="interactive-lift"
              disabled={isSubmitDisabled}
            >
              {authenticating ? 'Вход…' : 'Войти'}
            </Button>
            {showDevLogin && (
              <Button
                type="button"
                variant="outlined"
                size="large"
                onClick={handleDevLogin}
                disabled={authenticating}
              >
                Dev вход
              </Button>
            )}
            <Typography variant="body2" color="text.secondary" align="center">
              Получили токен от администратора?{' '}
              <MuiLink component={RouterLink} to={INITIAL_PASSWORD_ROUTE}>
                Установите пароль
              </MuiLink>
            </Typography>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
